/**
 * Preferences: what this account has decided, and how far it reaches.
 *
 * Deliberately **not** a second place to change chart appearance. Every setting
 * here is already edited where it is used - the ⚙ Chart menu, the theme toggle -
 * and a panel that duplicated those controls would be two surfaces to keep in
 * step and two answers to "which one is real". So this reads them back, says
 * where they are stored, keeps the startup destination (which has no natural
 * in-context control), and offers the reset action.
 *
 * The account block is here for the same reason: "what am I actually allowed to
 * do" had no answer anywhere in the UI, and `/api/me` has carried it all along.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { Atom } from '../../state/store.ts';
import { StoreController } from '../../state/controller.ts';
import { authState } from '../../auth/auth.ts';
import { userInitials } from '../../auth/profile.ts';
import { isRootUser, myAccess, myPermissions } from '../../state/store.ts';
import {
  applyChartPreferences,
  defaultPlotState,
  plotState,
  resetPlotStateToDefaults,
} from '../../state/plot-state.ts';
import {
  preferenceReach,
  preferences,
  resetPreferences,
} from '../../state/preferences.ts';
import { HOVER_TOOLTIP_KEY, hoverTooltipsOn, tooltipPosition } from '../../state/hover-tooltips.ts';
import { getTheme, setTheme, type Theme } from '../../ui/theme.ts';
import { setStartSection, startSection, type StartSection } from '../../state/landing.ts';
import { rerenderChartFromLast } from '../plot/plot-actions.ts';
import { resolveAccessCapabilities } from './access-capabilities.ts';

/** Open/closed, as an atom so the avatar menu can open it from anywhere. */
export const preferencesOpen = new Atom<boolean>(false);

export function openPreferences(): void {
  preferencesOpen.set(true);
}

/** One line per setting: what it is called, what it is, and whether it is theirs. */
interface Row {
  label: string;
  value: string;
  /** False when this is still the built-in default - nothing chosen yet. */
  chosen: boolean;
}

function onOff(value: boolean): string {
  return value ? 'on' : 'off';
}

/** "fixed, bottom right" - the tooltip setting as one short phrase. */
function tooltipWords(chart: Record<string, unknown>): string {
  if (!hoverTooltipsOn(chart)) return 'off';
  const pos = tooltipPosition(chart);
  const where = (c: string): string =>
    `${c[0] === 't' ? 'top' : 'bottom'} ${c[1] === 'l' ? 'left' : 'right'}`;
  if (pos.mode === 'follow') {
    return `follows the pointer, ${pos.side[0] === 't' ? 'above' : 'below'} ${pos.side[1] === 'l' ? 'left' : 'right'}`;
  }
  return `fixed, ${where(pos.corner)}${pos.fade ? '' : ', no fade'}`;
}

@customElement('preferences-panel')
export class PreferencesPanel extends LightElement {
  private store = new StoreController(this, [
    preferencesOpen,
    preferences,
    preferenceReach,
    plotState,
    authState,
    myPermissions,
    myAccess,
    isRootUser,
  ]);

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && preferencesOpen.value) this.close();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKeyDown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKeyDown);
  }

  private close(): void {
    preferencesOpen.set(false);
  }

  /**
   * Reset everything: the stored preferences and the chart in front of them.
   *
   * Both, because resetting one and not the other leaves the app looking
   * exactly as it did - which reads as a button that does nothing.
   */
  private async resetAll(): Promise<void> {
    await resetPreferences();
    setStartSection('home');
    resetPlotStateToDefaults();
    rerenderChartFromLast();
  }

  /**
   * What a *new* chart would start from - the remembered settings over the code
   * defaults.
   *
   * Deliberately not read from the chart on screen. That chart may have been
   * opened from somebody's link, or have had its ramp swapped for a diverging
   * one because it is a correlation matrix, and neither is this person's
   * default. A preferences panel that showed the current chart would be a
   * different answer to the question being asked.
   */
  private chartRows(): Row[] {
    const chosen = preferences.value.chart;
    const s = applyChartPreferences(defaultPlotState(), chosen);
    const style = s.style;
    const has = (key: string): boolean => key in chosen;
    return [
      { label: 'Text size', value: `${style.font_size}px`, chosen: has('font_size') },
      { label: 'Colour ramp', value: s.color_scale, chosen: has('color_scale') },
      { label: 'Line width', value: `${style.line_width}px`, chosen: has('line_width') },
      {
        label: 'Point size',
        value: `${style.scatter_point_size}px`,
        chosen: has('scatter_point_size'),
      },
      {
        label: 'Point opacity',
        value: style.scatter_alpha.toFixed(2),
        chosen: has('scatter_alpha'),
      },
      { label: 'Points on lines', value: onOff(style.show_points), chosen: has('show_points') },
      { label: 'Legend', value: onOff(style.show_legend), chosen: has('show_legend') },
      { label: 'Legend buttons', value: onOff(style.legend_tools), chosen: has('legend_tools') },
      {
        label: 'Tooltip',
        value: tooltipWords(chosen),
        chosen: [HOVER_TOOLTIP_KEY, 'tooltip_mode', 'tooltip_corner', 'tooltip_side', 'tooltip_fade'].some(has),
      },
      {
        label: 'Focus on hovered series',
        value: onOff(style.focus_hover),
        chosen: has('focus_hover'),
      },
      {
        label: 'Precision zoom',
        value: onOff(style.precision_zoom),
        chosen: has('precision_zoom'),
      },
      {
        label: 'Grid: shared Y range',
        value: onOff(style.grid_shared_y),
        chosen: has('grid_shared_y'),
      },
      {
        label: 'Grid: panels per row',
        value: style.grid_columns ? String(style.grid_columns) : 'automatic',
        chosen: has('grid_columns'),
      },
      { label: 'Heatmap ramp scaling', value: style.heat_scale, chosen: has('heat_scale') },
      {
        label: 'Correlation: lower triangle',
        value: onOff(style.corr_triangle),
        chosen: has('corr_triangle'),
      },
      {
        label: 'Pie: shape',
        value: style.pie_donut ? 'donut' : 'pie',
        chosen: has('pie_donut'),
      },
    ];
  }

  /** One honest line about how far these settings actually go. */
  private renderReach(): TemplateResult {
    const reach = preferenceReach.value;
    const stored = Object.keys(preferences.value.chart).length > 0 || preferences.value.theme;
    if (reach === 'unsaved') {
      return html`<p class="pref-reach warn">
        Saved in this browser, but not to your account - the last change could not
        be sent. It will be retried next time you change something.
      </p>`;
    }
    if (reach === 'browser') {
      return html`<p class="pref-reach">
        Stored in this browser. This deployment has no database to keep them with
        your account, so another machine starts from the defaults.
      </p>`;
    }
    return html`<p class="pref-reach">
      ${stored
        ? 'Stored with your account - they follow you to any browser you sign in from.'
        : 'Nothing stored yet. Change anything in the ⚙ Chart menu and it is remembered here.'}
    </p>`;
  }

  private renderAccount(): TemplateResult {
    const user = authState.value.user;
    const accessGroups = resolveAccessCapabilities(myPermissions.value, isRootUser.value, myAccess.value?.capabilities);
    const capabilities = accessGroups.flatMap((group) => group.capabilities);
    const availableCount = capabilities.filter((capability) => capability.available).length;
    return html`
      <section class="pref-section">
        <div class="pref-section-head">
          <span class="pref-section-index" aria-hidden="true">01</span>
          <div>
            <h3>Account</h3>
            <p>The account these preferences belong to.</p>
          </div>
        </div>
        <div class="pref-account-card">
          <span class="pref-account-avatar" aria-hidden="true">
            ${user ? userInitials(user) : '?'}
          </span>
          <span class="pref-account-id">
            <strong>${user?.name ?? '-'}</strong>
            <span title=${user?.email ?? ''}>${user?.email ?? '-'}</span>
          </span>
          <span class="pref-account-access">
            <span>Access</span>
            <strong>
              ${isRootUser.value && availableCount === capabilities.length
                ? html`<span class="pref-badge">Full access</span>`
                : `${availableCount} of ${capabilities.length} available`}
            </strong>
          </span>
        </div>

        <div class="pref-access-overview">
          <div class="pref-access-head">
            <div>
              <h4>Your access</h4>
              <p>What this account can use, change, and manage in Explorer.</p>
            </div>
            <div class="pref-access-legend" aria-label="Access status legend">
              <span class="is-available"><span aria-hidden="true">✓</span> Available</span>
              <span><span aria-hidden="true">−</span> No access</span>
            </div>
          </div>

          <div class="pref-access-groups">
            ${accessGroups.map((group) => {
              const groupAvailable = group.capabilities.filter(
                (capability) => capability.available,
              ).length;
              return html`<details class="pref-access-group">
                <summary>
                  <span>${group.label}</span>
                  <span class="pref-access-group-count">
                    ${groupAvailable} of ${group.capabilities.length} available
                  </span>
                  <span class="pref-access-chevron" aria-hidden="true"></span>
                </summary>
                <ul>
                  ${group.capabilities.map(
                    (capability) => html`<li
                      class=${capability.available ? 'is-available' : 'is-unavailable'}
                      aria-label="${capability.label}: ${capability.available ? 'available' : 'no access'}${capability.reason ? `. ${capability.reason}` : ''}"
                      title=${capability.reason ?? ''}
                    >
                      <span class="pref-capability-mark" aria-hidden="true">
                        ${capability.available ? '✓' : '−'}
                      </span>
                      <span class="pref-capability-label">${capability.label}</span>
                      <span class="pref-capability-state">
                        ${capability.available ? 'Available' : 'No access'}
                      </span>
                    </li>`,
                  )}
                </ul>
              </details>`;
            })}
          </div>
        </div>
      </section>
    `;
  }

  override render(): TemplateResult | typeof nothing {
    if (!preferencesOpen.value) return nothing;
    const theme = getTheme();
    const rows = this.chartRows();
    const savedChartCount = rows.filter((row) => row.chosen).length;
    const startup = startSection();
    const savedStartup = startup !== 'home';
    const savedCount =
      savedChartCount + (preferences.value.theme ? 1 : 0) + (savedStartup ? 1 : 0);
    return html`
      <div class="share-scrim" @click=${() => this.close()}></div>
      <div class="share-dialog pref-dialog" role="dialog" aria-modal="true" aria-label="Preferences">
        <header class="share-head pref-head">
          <div class="pref-title">
            <h2>Preferences</h2>
            <p>Your account and the choices Explorer remembers for you.</p>
          </div>
          <button class="catalog-btn" @click=${() => this.close()}>Done</button>
        </header>

        <div class="pref-body scroll-thin">
          ${this.renderAccount()}

          <section class="pref-section pref-settings">
            <div class="pref-section-head">
              <span class="pref-section-index" aria-hidden="true">02</span>
              <div>
                <h3>Settings</h3>
                <p>What Explorer will use when you start something new.</p>
              </div>
              <span class="pref-saved-count">
                ${savedCount ? `${savedCount} saved` : 'Using defaults'}
              </span>
            </div>

            ${this.renderReach()}

            <div class="pref-settings-block">
              <div class="pref-block-head">
                <div>
                  <h4>Startup</h4>
                  <p>Where a plain sign-in opens. Links still open their own destination.</p>
                </div>
                <span class="pref-setting-state ${savedStartup ? 'is-saved' : ''}">
                  ${savedStartup ? 'Saved' : 'Default'}
                </span>
              </div>
              <div class="pref-start-choice" role="group" aria-label="Startup destination">
                ${([
                  ['home', 'Home'],
                  ['explore', 'Explore'],
                ] as Array<[StartSection, string]>).map(
                  ([value, label]) => html`<button
                    type="button"
                    class="pref-start-btn ${startup === value ? 'active' : ''}"
                    aria-pressed=${startup === value ? 'true' : 'false'}
                    @click=${() => {
                      setStartSection(value);
                      this.requestUpdate();
                    }}
                  >
                    ${label}
                  </button>`,
                )}
              </div>
            </div>

            <div class="pref-settings-block">
              <div class="pref-block-head">
                <div>
                  <h4>Theme</h4>
                  <p>Applied throughout Explorer.</p>
                </div>
                <span class="pref-setting-state ${preferences.value.theme ? 'is-saved' : ''}">
                  ${preferences.value.theme ? 'Saved' : 'Default'}
                </span>
              </div>
              <div class="pref-theme">
                ${(['dark', 'light'] as Theme[]).map(
                  (name) => html`<button
                    type="button"
                    class="pref-theme-btn ${theme === name ? 'active' : ''}"
                    aria-pressed=${theme === name ? 'true' : 'false'}
                    @click=${() => {
                      setTheme(name);
                      // uPlot draws its colours from CSS variables at build time, so
                      // the chart has to be rebuilt to follow the theme.
                      rerenderChartFromLast();
                      this.requestUpdate();
                    }}
                  >
                    <span aria-hidden="true">${name === 'light' ? '☀' : '☾'}</span>
                    ${name === 'light' ? 'Light' : 'Dark'}
                  </button>`,
                )}
              </div>
            </div>

            <div class="pref-settings-block">
              <div class="pref-block-head">
                <div>
                  <h4>Chart appearance</h4>
                  <p>Changed from the ⚙ Chart menu on a chart.</p>
                </div>
                <span class="pref-setting-state ${savedChartCount ? 'is-saved' : ''}">
                  ${savedChartCount ? `${savedChartCount} saved` : 'Defaults'}
                </span>
              </div>
              <p class="pref-note">
                A link or saved chart can still carry its own appearance, so a shared
                view always opens as its author intended.
              </p>
              <dl class="pref-rows pref-chart-rows">
                ${rows.map(
                  (row) => html`<div class="pref-row">
                    <dt>${row.label}</dt>
                    <dd class=${row.chosen ? '' : 'is-default'}>
                      <span>${row.value}</span>
                      <span class="pref-row-state ${row.chosen ? 'is-saved' : ''}">
                        ${row.chosen ? 'Saved' : 'Default'}
                      </span>
                    </dd>
                  </div>`,
                )}
              </dl>
            </div>
          </section>
        </div>

        <footer class="pref-foot">
          <span>Reset removes every saved choice from this account.</span>
          <button class="catalog-btn pref-reset" @click=${() => void this.resetAll()}>
            Reset to defaults
          </button>
        </footer>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'preferences-panel': PreferencesPanel;
  }
}
